import type { ProviderStorageBoxRow } from '../types/storageBox';
import type { Category } from '../types/foundObject';
import {
  canonicalizeAttributeKey,
  normalizeAttributeText,
  resolveObjectAttributeValue,
  categoryAttributesRecord,
} from './objectAttributeDisplay';

/** Segments « fourre-tout » dans le nom de boîte (ex. TEL-autre). */
const CATCH_ALL_BOX_SUFFIXES = new Set([
  'autre',
  'autres',
  'other',
  'others',
  'divers',
  'misc',
  'generique',
  'generic',
]);

/** Marque déclarée → variantes reconnues dans le suffixe de boîte. */
const BRAND_TO_BOX_TOKENS: Record<string, string[]> = {
  iphone: ['iphone', 'apple'],
  samsung: ['samsung'],
  huawei: ['huawei'],
  xiaomi: ['xiaomi', 'redmi'],
  google: ['google', 'pixel'],
  oneplus: ['oneplus'],
  oppo: ['oppo'],
  honor: ['honor'],
};

/**
 * Extrait la marque renseignée sur l'objet (clé brand / marque / label catégorie).
 */
export function extractObjectBrandValue(
  objectAttributes: Record<string, unknown> | undefined,
  category?: Pick<Category, 'attributes'> | null
): string {
  if (!objectAttributes) return '';
  const defs = categoryAttributesRecord(category);

  const brandKeys = Object.keys(defs).filter(
    (key) => canonicalizeAttributeKey(key) === 'brand'
  );
  if (brandKeys.length === 0) {
    brandKeys.push('brand', 'marque', 'Marque');
  }

  for (const key of brandKeys) {
    const raw = resolveObjectAttributeValue(objectAttributes, key, defs[key]);
    const text = normalizeAttributeText(
      typeof raw === 'string' || typeof raw === 'number' ? String(raw) : ''
    );
    if (text) return text;
  }

  for (const [key, value] of Object.entries(objectAttributes)) {
    if (value === undefined || value === null || value === '') continue;
    if (canonicalizeAttributeKey(key) !== 'brand') continue;
    const text = normalizeAttributeText(String(value));
    if (text) return text;
  }

  return '';
}

/** Tokens normalisés pour comparer marque objet ↔ suffixe boîte. */
export function brandTokensForBoxMatch(brandRaw: string): string[] {
  const base = normalizeAttributeText(brandRaw);
  if (!base) return [];

  const tokens = new Set<string>([base]);
  for (const [canonical, aliases] of Object.entries(BRAND_TO_BOX_TOKENS)) {
    if (aliases.some((alias) => base.includes(alias) || alias.includes(base))) {
      tokens.add(canonical);
      aliases.forEach((alias) => tokens.add(alias));
    }
  }
  return Array.from(tokens);
}

/**
 * Partie après le préfixe catégorie (TEL-Iphone → iphone) ou dernier segment du nom.
 */
export function getStorageBoxNameSuffix(
  boxName: string | null | undefined,
  categoryPrefix?: string | null
): string {
  const raw = String(boxName ?? '').trim();
  if (!raw) return '';

  const normalizedFull = normalizeAttributeText(raw);
  const prefix = String(categoryPrefix ?? '')
    .replace(/[^A-Za-z]/g, '')
    .toLowerCase();
  if (prefix.length >= 2) {
    const prefixPattern = new RegExp(`^${prefix}-(.+)$`, 'i');
    const m = normalizedFull.match(prefixPattern);
    if (m?.[1]) {
      return normalizeAttributeText(m[1].replace(/-/g, ' '));
    }
  }

  const dashIdx = raw.indexOf('-');
  if (dashIdx >= 0) {
    return normalizeAttributeText(raw.slice(dashIdx + 1).replace(/-/g, ' '));
  }

  return normalizedFull;
}

/** Score de proximité marque ↔ nom de boîte (0 = pas de lien). */
export function scoreStorageBoxBrandMatch(
  box: Pick<ProviderStorageBoxRow, 'name'>,
  brandTokens: string[],
  categoryPrefix?: string | null
): number {
  if (brandTokens.length === 0) return 0;

  const suffix = getStorageBoxNameSuffix(box.name, categoryPrefix);
  if (!suffix || /^\d+$/.test(suffix.replace(/\s/g, ''))) return 0;

  if (CATCH_ALL_BOX_SUFFIXES.has(suffix)) {
    return 120;
  }

  for (const brand of brandTokens) {
    if (suffix === brand) return 1000;
    if (suffix.includes(brand) || brand.includes(suffix)) return 900;
  }

  return 0;
}
